import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import Header from '../../components/Header';
import { createEntry, updateEntry, deleteEntry, listRegistryTypes } from '../../api/metaRegistriesApi';
import { useRegistryBrowser } from './hooks/useRegistryBrowser';
import { useEntryDetails } from './hooks/useEntryDetails';
import { useDiagramLinking } from './hooks/useDiagramLinking';
import { filterEntries } from './utils/filterUtils';
import { createStyles } from './styles/registryStyles';
import { resolveRegistryUiProfile } from './registryUiProfile';
import RegistryFilter from './components/RegistryFilter';
import RegistryList from './components/RegistryList';
import RegistryDetailsPanel from './components/RegistryDetailsPanel';
import EntryModal from './components/EntryModal';
import LinkChildModal from './components/LinkChildModal';
import type { MetaRegistryEntry } from '../../types/systems';
import NavigationRail, { NAV_RAIL_COLLAPSED_WIDTH } from '../../components/navigation/NavigationRail';
import { useProject } from '../../context/ProjectContext';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const SLUG_ICONS: Record<string, React.ReactNode> = {
  information_systems: <DnsOutlinedIcon fontSize="small" />,
  'business-capabilities': <ExtensionOutlinedIcon fontSize="small" />,
  'bcm-maps': <MapOutlinedIcon fontSize="small" />,
};
const FALLBACK_ICONS: React.ReactNode[] = [
  <CategoryOutlinedIcon fontSize="small" />,
  <LayersOutlinedIcon fontSize="small" />,
  <HubOutlinedIcon fontSize="small" />,
  <SchemaOutlinedIcon fontSize="small" />,
  <ListAltOutlinedIcon fontSize="small" />,
  <WidgetsOutlinedIcon fontSize="small" />,
  <AssignmentOutlinedIcon fontSize="small" />,
  <AccountTreeOutlinedIcon fontSize="small" />,
];
const getRegistryIcon = (s: string, i = 0): React.ReactNode =>
  SLUG_ICONS[s] ?? FALLBACK_ICONS[i % FALLBACK_ICONS.length];

const RegistryBrowser: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, slug } = useParams<{ projectId: string; slug: string }>();
  const { theme } = useTheme();
  const { currentProject } = useProject();
  const [railWidth, setRailWidth] = useState(NAV_RAIL_COLLAPSED_WIDTH);
  const [allRegistryTypes, setAllRegistryTypes] = useState<{ slug: string; name: string }[]>([]);

  const [query, setQuery] = useState('');
  const [filterKind, setFilterKind] = useState('all');
  const [selected, setSelected] = useState<MetaRegistryEntry | null>(null);

  // Modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [createParent, setCreateParent] = useState<MetaRegistryEntry | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MetaRegistryEntry | null>(null);
  const [linkChildOpen, setLinkChildOpen] = useState(false);
  const [linkChildParent, setLinkChildParent] = useState<MetaRegistryEntry | null>(null);

  const relatedRef = useRef<HTMLDivElement>(null);
  const modulesRef = useRef<HTMLDivElement>(null);
  const autoSelectDoneRef = useRef(false);

  const { registryType, kinds, entries, loading, error, reload } =
    useRegistryBrowser(projectId!, slug!, null);

  const uiProfile = resolveRegistryUiProfile(registryType);

  const {
    relLoading,
    relError,
    related,
    setRelated,
    allDiagrams,
    loadFullEntryInfo,
    children,
    childrenLoading,
    setChildren,
    infrastructure,
    infraLoading,
    namespaceDetails,
    namespaceLoading,
  } = useEntryDetails(selected, slug!, uiProfile, projectId);

  const {
    linking,
    linkError,
    selectedDiagramId,
    setSelectedDiagramId,
    handleLinkDiagram,
    handleUnlinkDiagram
  } = useDiagramLinking();

  // Auto-select entry when navigated from a C4 diagram element
  const autoSelectName = (location.state as { selectByName?: string } | null)?.selectByName ?? null;
  useEffect(() => {
    if (!autoSelectName || autoSelectDoneRef.current || loading || !entries.length) return;
    const entry = entries.find(
      (e) => e.name === autoSelectName || e.code === autoSelectName
    );
    if (!entry) return;
    autoSelectDoneRef.current = true;
    loadFullEntryInfo(entry.id).then((full) => setSelected(full ?? entry));
  }, [autoSelectName, entries, loading, loadFullEntryInfo]);

  useEffect(() => {
    if (projectId) listRegistryTypes(projectId).then(setAllRegistryTypes).catch(() => {});
  }, [projectId]);

  const railItems = useMemo(() => {
    const dedicated: Record<string, string> = { information_systems: 'systems' };
    const seen = new Set<string>();
    const role = currentProject?.role;
    const items = [
      {
        id: 'diagrams', label: 'Диаграммы',
        icon: <GridViewOutlinedIcon fontSize="small" />, active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId!)}`),
      },
      ...allRegistryTypes
        .filter((rt) => { if (seen.has(rt.slug)) return false; seen.add(rt.slug); return true; })
        .map((rt, idx) => {
          const path = rt.slug === 'bcm-maps'
            ? `/projects/${encodeURIComponent(projectId!)}/bcm`
            : dedicated[rt.slug]
            ? `/projects/${encodeURIComponent(projectId!)}/${dedicated[rt.slug]}`
            : `/projects/${encodeURIComponent(projectId!)}/registry/${encodeURIComponent(rt.slug)}`;
          return {
            id: rt.slug, label: rt.name, icon: getRegistryIcon(rt.slug, idx),
            active: rt.slug === slug,
            onClick: () => navigate(path),
          };
        }),
      {
        id: 'health-map', label: 'Карта работоспособности',
        icon: <MonitorHeartOutlinedIcon fontSize="small" />, active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId!)}/health`),
      },
    ];
    if (role === 'owner' || role === 'admin') {
      items.push({
        id: 'settings', label: 'Настройки пространства',
        icon: <SettingsOutlinedIcon fontSize="small" />, active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId!)}/settings`),
      });
    }
    return items;
  }, [allRegistryTypes, projectId, slug, navigate, currentProject?.role]);

  const styles = createStyles(theme);

  const handleEntryClick = async (entry: MetaRegistryEntry) => {
    if (registryType?.attr_schema?.open_as_board) {
      navigate(`/projects/${encodeURIComponent(projectId!)}/bcm/${encodeURIComponent(entry.id)}`);
      return;
    }
    const full = await loadFullEntryInfo(entry.id);
    setSelected(full || entry);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteEntry(projectId!, slug!, id);
      setSelected(null);
      reload();
    } catch (e) {
      console.error('Ошибка удаления:', e);
    }
  };

  const handleDeleteChild = async (childId: string) => {
    try {
      await deleteEntry(projectId!, slug!, childId);
      setChildren(prev => prev.filter(c => c.id !== childId));
      reload();
    } catch (e) {
      console.error('Ошибка удаления дочерней записи:', e);
    }
  };

  const handleChildClick = async (child: MetaRegistryEntry) => {
    const full = await loadFullEntryInfo(child.id);
    setSelected(full || child);
  };

  const handleCreate = async (data: any) => {
    await createEntry(projectId!, slug!, data);
    reload();
  };

  const handleEdit = async (data: any) => {
    if (!editTarget) return;
    const updated = await updateEntry(projectId!, slug!, editTarget.id, data);
    // Refresh selected if it's the same entry
    if (selected?.id === editTarget.id) {
      const full = await loadFullEntryInfo(editTarget.id);
      setSelected(full || updated);
    }
    reload();
  };

  const openCreateChild = (parent: MetaRegistryEntry) => {
    setCreateParent(parent);
    setCreateOpen(true);
  };

  const openLinkExisting = (parent: MetaRegistryEntry) => {
    setLinkChildParent(parent);
    setLinkChildOpen(true);
  };

  const handleLinkExisting = async (entry: MetaRegistryEntry) => {
    if (!linkChildParent) return;
    // PUT is a full replace — preserve all existing fields, only change parent_id
    await updateEntry(projectId!, slug!, entry.id, {
      code: entry.code,
      name: entry.name,
      description: entry.description,
      kind_slug: (entry as any).kind_slug,
      tags: entry.tags,
      attributes: (entry as any).attributes ?? {},
      parent_id: linkChildParent.id,
    } as any);
    reload();
    // Open the parent so the newly linked child is visible in its children list
    const full = await loadFullEntryInfo(linkChildParent.id);
    setSelected(full || linkChildParent);
  };

  const openEdit = (entry: MetaRegistryEntry) => {
    setEditTarget(entry);
    setEditOpen(true);
  };

  const filteredEntries = filterEntries(entries, filterKind, query);

  return (
    <>
      <Header title="Aspect" height={56} />
      <NavigationRail
        ariaLabel="Реестры пространства"
        theme={theme}
        items={railItems}
        onExpandedChange={setRailWidth}
      />
      <div style={{ ...styles.wrap, paddingLeft: railWidth + 24, transition: 'padding-left .16s ease' }}>
        <div style={styles.content}>
          <h1 style={styles.title}>
            {registryType?.name ?? slug} — пространство: {projectId}
          </h1>

          <RegistryFilter
            filterKind={filterKind}
            setFilterKind={setFilterKind}
            query={query}
            setQuery={setQuery}
            kinds={kinds}
            onNavigateBack={() => navigate(`/projects/${encodeURIComponent(projectId!)}`)}
            onCreateNew={() => { setCreateParent(null); setCreateOpen(true); }}
            theme={theme}
            styles={styles}
            breadcrumb={null}
          />

          <RegistryList
            entries={filteredEntries}
            kinds={kinds}
            theme={theme}
            styles={styles}
            onEntryClick={handleEntryClick}
            onDelete={handleDelete}
            onDrillInto={null}
            loading={loading}
            error={error}
          />
        </div>
      </div>

      <RegistryDetailsPanel
        selected={selected}
        kinds={kinds}
        theme={theme}
        styles={styles}
        onClose={() => setSelected(null)}
        relatedRef={relatedRef}
        modulesRef={modulesRef}
        related={related}
        relLoading={relLoading}
        relError={relError}
        allDiagrams={allDiagrams}
        selectedDiagramId={selectedDiagramId}
        setSelectedDiagramId={setSelectedDiagramId}
        linking={linking}
        linkError={linkError}
        onLinkDiagram={handleLinkDiagram}
        onUnlinkDiagram={handleUnlinkDiagram}
        onNavigate={navigate}
        children={children}
        childrenLoading={childrenLoading}
        onChildClick={handleChildClick}
        onDeleteChild={handleDeleteChild}
        onDelete={handleDelete}
        onEdit={openEdit}
        onAddChild={openCreateChild}
        onLinkExisting={openLinkExisting}
        onRefresh={reload}
        registrySlug={slug}
        projectId={projectId}
        infrastructure={infrastructure}
        infraLoading={infraLoading}
        namespaceDetails={namespaceDetails}
        namespaceLoading={namespaceLoading}
        allEntries={entries}
        uiProfile={uiProfile}
      />

      {/* Create modal */}
      <EntryModal
        isOpen={createOpen}
        onClose={() => { setCreateOpen(false); setCreateParent(null); }}
        onSubmit={handleCreate}
        theme={theme}
        kinds={kinds}
        parentEntry={createParent}
        allEntries={entries}
        initialValues={null}
        title={createParent ? `Добавить дочернюю к «${createParent.name}»` : 'Создать запись'}
        submitLabel="Создать"
        uiProfile={uiProfile}
        projectId={projectId}
      />

      {/* Link existing child modal */}
      <LinkChildModal
        isOpen={linkChildOpen}
        onClose={() => { setLinkChildOpen(false); setLinkChildParent(null); }}
        onLink={handleLinkExisting}
        theme={theme}
        allEntries={entries}
        currentEntry={linkChildParent}
        currentChildren={children}
      />

      {/* Edit modal */}
      <EntryModal
        isOpen={editOpen}
        onClose={() => { setEditOpen(false); setEditTarget(null); }}
        onSubmit={handleEdit}
        theme={theme}
        kinds={kinds}
        parentEntry={null}
        allEntries={entries}
        initialValues={editTarget}
        title={`Редактировать «${editTarget?.name ?? ''}»`}
        submitLabel="Сохранить"
        uiProfile={uiProfile}
        projectId={projectId}
      />
    </>
  );
};

export default RegistryBrowser;
